import React from 'react'
import dynamic from 'next/dynamic'
import * as ReactDOMServer from 'react-dom/server'
import { formatWithComma, thousandToMillion } from '@/lib/number'
import { assertNever } from '@/lib/ts-utils'
import {
  CompanyChartData,
  IPOFundraiseCategory as IPOCategory,
  PrivateFundraiseCategory,
} from '../../../../types/api'
import { CustomTooltip, TooltipItem } from './CustomTooltip'
import { colors as plumeColors } from 'plume-ui'
const Chart = dynamic(() => import('react-apexcharts'), { ssr: false })

type RatioData = { [label: string]: number }

const DonutChart: React.FC<{
  title: string
  data: RatioData
  colorFn: (s: string) => string
  tooltipFn: typeof tooltipHTMLOfFundraise | typeof tooltipHTMLOfShare
  className?: string
}> = (props) => {
  const categories = Object.entries(props.data)
    .map(([k, v]) => (v ? k : undefined))
    .filter((k) => k !== undefined) as string[]

  const colors = categories.map(props.colorFn)

  const values = Object.values(props.data).filter((v) => v !== null && v !== 0)
  const total = values.reduce((a, b) => a + b, 0)

  const options = {
    labels: categories,
    dataLabels: {
      enabled: false,
    },
    stroke: {
      width: 2,
    },
    legend: {
      show: categories.length > 0,
      markers: {
        fillColors: colors,
      },
    },
    fill: {
      colors,
    },
    title: {
      text: props.title,
    },
    noData: {
      text: '表示するデータがありません',
      align: 'left',
      style: {
        color: plumeColors.gray70,
        fontSize: '13px',
      },
    },
    tooltip: {
      custom: ({ seriesIndex }: { seriesIndex: number }) => {
        return props.tooltipFn(props.data, total, seriesIndex, props.colorFn)
      },
      theme: 'light',
    },
    plotOptions: {
      pie: {
        donut: {
          size: '50%',
        },
        expandOnClick: false,
      },
    },
  }

  return (
    <div className={props.className}>
      <Chart type="donut" width={320} series={values} options={options} />
    </div>
  )
}

export const PrivateFundraiseChart: React.FC<{
  data: CompanyChartData['privateFundraiseRatio']
}> = (props) => {
  return (
    <DonutChart
      title="総調達額の内訳(過去分含む）"
      data={props.data as RatioData}
      colorFn={colorOfPrivateFundingCategory as (s: string) => string}
      tooltipFn={tooltipHTMLOfFundraise}
    />
  )
}

export const IPOFundraiseChart: React.FC<{
  data: CompanyChartData['ipoFundraiseRatio']
  className?: string
}> = (props) => {
  return (
    <DonutChart
      title="総調達額の内訳（IPO前）"
      data={props.data as RatioData}
      colorFn={colorOfIPOCategory as (s: string) => string}
      tooltipFn={tooltipHTMLOfFundraise}
      className={props.className}
    />
  )
}

export const IPOShareChart: React.FC<{
  data: CompanyChartData['ipoShareRatio']
  className?: string
}> = (props) => {
  return (
    <DonutChart
      title="株式比率（IPO前）"
      data={props.data as RatioData}
      colorFn={colorOfIPOCategory as (s: string) => string}
      tooltipFn={tooltipHTMLOfShare}
      className={props.className}
    />
  )
}

const tooltipHTMLOfFundraise = (
  ratio: RatioData,
  total: number,
  targetIndex: number,
  colorFn: (s: string) => string
) => {
  if (!ratio) {
    return null
  }
  const target = Object.entries(ratio)[targetIndex] as [
    PrivateFundraiseCategory,
    number,
  ]

  const percentage = Math.round((target[1] / total) * 100 * 10) / 10

  const items: TooltipItem[] = [
    {
      title: target[0],
      color: colorFn(target[0]),
      dict: [
        {
          label: '調達額',
          value: formatWithComma(thousandToMillion(target[1])),
          unit: '百万円',
        },
        {
          label: '割合',
          value: percentage,
          unit: '%',
        },
      ],
    },
  ]

  return ReactDOMServer.renderToStaticMarkup(<CustomTooltip items={items} />)
}

const tooltipHTMLOfShare = (
  ratio: RatioData,
  total: number,
  targetIndex: number,
  colorFn: (s: string) => string
) => {
  if (!ratio) {
    return null
  }
  const target = Object.entries(ratio)[targetIndex] as [
    PrivateFundraiseCategory,
    number,
  ]

  const percentage = Math.round((target[1] / total) * 100 * 10) / 10

  const item: TooltipItem[] = [
    {
      title: target[0],
      color: colorFn(target[0]),
      dict: [
        {
          label: '割合',
          value: percentage,
          unit: '%',
        },
      ],
    },
  ]

  return ReactDOMServer.renderToStaticMarkup(<CustomTooltip items={item} />)
}

const colorOfPrivateFundingCategory = (c: PrivateFundraiseCategory) => {
  switch (c) {
    case 'VC':
      return '#000667'
    case '金融機関':
      return '#004db0'
    case '事業法人':
      return '#008dd2'
    case '海外VC':
      return '#2dbaf0'
    case '海外金融機関':
      return '#5ed8e0'
    case '海外事業法人':
      return '#b4e3e4'
    case '個人/個人会社':
      return '#8797ae'
    case 'その他':
      return '#d7dce4'
    default:
      return assertNever(c)
  }
}

const colorOfIPOCategory = (c: IPOCategory) => {
  switch (c) {
    case 'VC':
      return '#000667'
    case '事業会社':
      return '#008dd2'
    case '個人':
      return '#8797ae'
    default:
      return assertNever(c)
  }
}
